import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban, Clock, Fuel, Layers, Route, TrendingUp, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';
import { formatBps, formatKm, formatMinor, formatPlate, formatWhen } from '@/lib/format';
import { api, ApiError } from '@/api/client';
import { qk } from '@/api/keys';
import { useCandidates } from '@/api/hooks/orders';
import { useDrivers } from '@/api/hooks/catalog';
import type { AssignmentInput, DispatchCandidate, TripDetail } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, LoadingBlock } from '@/components/ui/State';
import { Checkbox, Field, Select } from '@/components/ui/Field';
import { toast } from '@/stores/toast';
import { LoadBar } from './LoadBar';
import { SingleOrderReasonField } from './SingleOrderReasonField';

/** Над това бусът е пълен и курс с една заявка не иска причина — същото число като в сървъра. */
const FULL_PCT = 85;

/**
 * „Кой бус за тази заявка." Подредбата е от сървъра: първо бусовете, в които
 * заявката се побира в ВЕЧЕ планиран курс — така бусът не тръгва полупразен —
 * после свободните по празни километри и марж. Бус, в който тя не се побира
 * по тегло, е забранен: претоварен бус е глоба и опасност.
 */
export function AssignDialog({
  orderId,
  orderNumber,
  open,
  onClose,
}: {
  orderId: string;
  orderNumber: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useCandidates(open ? orderId : undefined);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<DispatchCandidate | null>(null);
  const [joinTrip, setJoinTrip] = useState(true);
  const [dispatchNow, setDispatchNow] = useState(true);
  const [reason, setReason] = useState('');
  const [needsReason, setNeedsReason] = useState(false);
  const [volumeOk, setVolumeOk] = useState(false);
  const [volumeWarning, setVolumeWarning] = useState<string | null>(null);
  const [driverId, setDriverId] = useState('');
  const drivers = useDrivers();

  const candidates = useMemo(() => data?.candidates ?? [], [data]);
  const order = data?.order ?? null;

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setReason('');
      setNeedsReason(false);
      setVolumeOk(false);
      setVolumeWarning(null);
    }
  }, [open]);

  const choose = (candidate: DispatchCandidate) => {
    setSelected(candidate);
    setJoinTrip(candidate.openTrip !== null && candidate.capacity.fitsWeight !== false);
    setNeedsReason(false);
    setVolumeWarning(null);
    setVolumeOk(false);
    setDriverId(candidate.driver?.id ?? '');
  };

  /** Нов курс само с тази заявка, а бусът не е пълен — правилото иска причина. */
  const fill = selected ? fillOf(selected) : null;
  const singleOrder =
    selected !== null &&
    dispatchNow &&
    !(joinTrip && selected.openTrip) &&
    !(fill !== null && fill.full);
  const showReason = singleOrder || needsReason;

  const assign = useMutation({
    meta: { silent: true },
    mutationFn: (input: AssignmentInput) => api.post<TripDetail>('/dispatch/assign', input),
    onSuccess: (trip) => {
      void queryClient.invalidateQueries({ queryKey: qk.orders.all });
      void queryClient.invalidateQueries({ queryKey: qk.trips.all });
      void queryClient.invalidateQueries({ queryKey: qk.board.all });
      void queryClient.invalidateQueries({ queryKey: qk.dispatch.all });
      toast.ok(dispatchNow ? 'Курсът е изпратен към шофьора' : t.dispatch.assigned, `${trip.number} · ${selected?.fleetNo ?? ''}`);
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'SINGLE_ORDER_REASON_REQUIRED') {
        setNeedsReason(true);
        return;
      }
      if (error instanceof ApiError && error.code === 'VOLUME_EXCEEDED') {
        setVolumeWarning(error.message);
        return;
      }
      toast.error(error instanceof ApiError ? error.message : t.errors.unknown);
    },
  });

  const confirm = () => {
    if (!selected) return;
    assign.mutate({
      orderId,
      vehicleId: selected.vehicleId,
      ...(driverId ? { driverId } : {}),
      ...(joinTrip && selected.openTrip ? { tripId: selected.openTrip.id } : {}),
      dispatchNow,
      ...(showReason && reason.trim().length >= 3 ? { singleOrderReason: reason.trim() } : {}),
      ...(volumeOk ? { allowVolumeOverflow: true } : {}),
    });
  };

  const reasonMissing = showReason && reason.trim().length < 3;
  /** Курс не тръгва към шофьор, който не е избран. */
  const driverMissing = dispatchNow && !driverId;
  const volumeBlocked = volumeWarning !== null && !volumeOk;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${t.dispatch.candidatesFor} — ${orderNumber}`}
      description={
        order
          ? [
              order.from && order.to ? `${order.from} → ${order.to}` : null,
              order.weightKg !== null ? `${order.weightKg} кг` : 'без тегло',
              order.volumeM3 !== null ? `${order.volumeM3.toLocaleString('bg-BG')} m³` : 'без обем',
              order.pickupAt ? `товарене ${formatWhen(order.pickupAt)}` : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : t.board.advisory
      }
      width="xl"
      footer={
        selected ? (
          <>
            <Checkbox
              className="mr-auto"
              label="Изпрати веднага към телефона на шофьора"
              checked={dispatchNow}
              onChange={(e) => setDispatchNow(e.target.checked)}
            />
            <Button variant="ghost" onClick={() => setSelected(null)}>
              {t.common.back}
            </Button>
            <Button
              variant="primary"
              onClick={confirm}
              loading={assign.isPending}
              disabled={reasonMissing || volumeBlocked || driverMissing}
            >
              {joinTrip && selected.openTrip ? `Качи в ${selected.openTrip.number}` : `Назначи ${selected.fleetNo}`}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            {t.common.cancel}
          </Button>
        )
      }
    >
      {isLoading ? <LoadingBlock /> : null}
      {isError ? <EmptyState title={t.errors.server} /> : null}
      {!isLoading && !isError && candidates.length === 0 ? (
        <EmptyState
          title={t.dispatch.noCandidates}
          body="Проверете срока, изтеклите документи и дали някой бус не е в сервиз."
        />
      ) : null}

      {order && (order.weightKg === null || order.volumeM3 === null) ? (
        <p className="mb-3 rounded-md border border-warn/40 bg-warn-soft/40 px-3 py-2 text-xs text-warn">
          Заявката няма {order.weightKg === null ? 'тегло' : ''}
          {order.weightKg === null && order.volumeM3 === null ? ' и ' : ''}
          {order.volumeM3 === null ? 'обем' : ''} — проверката дали се побира в буса е непълна. Въведете ги в
          заявката.
        </p>
      ) : null}

      <ul className="space-y-2">
        {candidates.map((candidate) => {
          const blocked = !candidate.allowed;
          const isSelected = selected?.vehicleId === candidate.vehicleId;
          const cap = candidate.capacity;
          return (
            <li key={candidate.vehicleId}>
              <button
                type="button"
                disabled={blocked}
                onClick={() => choose(candidate)}
                className={cn(
                  'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                  blocked
                    ? 'cursor-not-allowed border-danger/40 bg-danger-soft/40 opacity-80'
                    : isSelected
                      ? 'border-accent bg-accent-soft'
                      : 'border-line hover:border-accent/60 hover:bg-raised',
                )}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-mono text-xs text-ink-faint">{candidate.rank}</span>
                  <span className="font-display text-sm font-semibold">{candidate.fleetNo}</span>
                  <span className="font-mono text-xs text-ink-dim">{formatPlate(candidate.plate)}</span>
                  <span className="text-sm text-ink-dim">{candidate.driver?.name ?? 'без шофьор'}</span>
                  {blocked ? (
                    <Badge tone="danger">
                      <Ban className="size-3" />
                      {t.dispatch.blocked}
                    </Badge>
                  ) : candidate.openTrip && cap.fitsWeight !== false ? (
                    <Badge tone="accent">
                      <Layers className="size-3" />
                      побира се в {candidate.openTrip.number}
                    </Badge>
                  ) : null}
                  {candidate.expectedMarginMinor !== null ? (
                    <span className="ml-auto font-mono text-sm">
                      {formatMinor(candidate.expectedMarginMinor, candidate.ccy)}{' '}
                      <span className="text-xs text-ink-faint">({formatBps(candidate.expectedMarginBps)})</span>
                    </span>
                  ) : null}
                </div>

                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <LoadBar label="Тегло" value={cap.afterKg} max={cap.payloadKg} unit="кг" />
                  <LoadBar label="Обем" value={cap.afterM3} max={cap.cargoVolumeM3} unit="m³" digits={1} />
                </div>

                {candidate.openTrip ? (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-ink-dim">
                    <Route className="mt-0.5 size-3 shrink-0" />
                    <span>
                      Отворен курс <b className="font-mono text-ink">{candidate.openTrip.number}</b> (
                      {candidate.openTrip.statusLabel}): {candidate.openTrip.from ?? '—'} → {candidate.openTrip.to ?? '—'}
                      {' · '}
                      {candidate.openTrip.orders} {candidate.openTrip.orders === 1 ? 'заявка' : 'заявки'}
                      {candidate.openTrip.plannedStartAt ? ` · ${formatWhen(candidate.openTrip.plannedStartAt)}` : ''}
                    </span>
                  </p>
                ) : null}

                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
                  <Metric icon={Fuel} label={t.dispatch.emptyKm} value={formatKm(candidate.emptyKm)} />
                  <Metric
                    icon={TrendingUp}
                    label={t.dispatch.cost}
                    value={formatMinor(candidate.estimatedCostMinor, candidate.ccy)}
                  />
                  <Metric
                    icon={Clock}
                    label="Свободен от"
                    value={`${formatWhen(candidate.freeAt)}${candidate.freeAtPlace ? ` · ${candidate.freeAtPlace}` : ''}`}
                  />
                </dl>

                {candidate.blockers.length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {candidate.blockers.map((block) => (
                      <li key={block.code} className="flex items-start gap-1.5 text-xs text-danger">
                        <Ban className="mt-0.5 size-3 shrink-0" />
                        {block.message}
                      </li>
                    ))}
                  </ul>
                ) : null}

                {candidate.warnings.length > 0 ? (
                  <ul className="mt-1.5 space-y-1">
                    {candidate.warnings.map((warning) => (
                      <li key={warning} className="flex items-start gap-1.5 text-xs text-warn">
                        <TriangleAlert className="mt-0.5 size-3 shrink-0" />
                        {warning}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>

      {selected ? (
        <div className="mt-3 space-y-3">
          <Field
            label="Шофьор"
            hint={driverMissing ? 'Изберете шофьор — курсът не се изпраща без него.' : undefined}
          >
            <Select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              options={[
                { value: '', label: '— без шофьор —' },
                /** Свободните първо; заетите личат с курса си. */
                ...(drivers.data?.items ?? [])
                  .filter((d) => d.status === 'active')
                  .sort((a, b) => Number(Boolean(a.activeTrip)) - Number(Boolean(b.activeTrip)))
                  .map((d) => ({
                    value: d.id,
                    label: d.activeTrip ? `${d.fullName} — на курс ${d.activeTrip.number}` : `${d.fullName} — свободен`,
                  })),
              ]}
            />
          </Field>
          {selected.openTrip && selected.capacity.fitsWeight !== false && selected.newTripAllowed ? (
            <div className="flex flex-wrap gap-2">
              <ModeButton active={joinTrip} onClick={() => setJoinTrip(true)}>
                Качи в {selected.openTrip.number} ({selected.openTrip.orders + 1} заявки)
              </ModeButton>
              <ModeButton active={!joinTrip} onClick={() => setJoinTrip(false)}>
                Нов курс само с тази заявка
              </ModeButton>
            </div>
          ) : null}

          {volumeWarning ? (
            <div className="rounded-lg border border-warn/40 bg-warn-soft/40 p-3 text-xs text-warn">
              <p>{volumeWarning}</p>
              <Checkbox
                className="mt-2"
                label="Обемът е приблизителен — проверено е, че товарът се побира"
                checked={volumeOk}
                onChange={(e) => setVolumeOk(e.target.checked)}
              />
            </div>
          ) : null}

          {showReason ? (
            <SingleOrderReasonField
              value={reason}
              onChange={setReason}
              loadNote={fill ? fill.note : undefined}
            />
          ) : (
            <p className="rounded-md border border-line bg-raised px-3 py-2 text-xs text-ink-dim">
              {t.dispatch.assignConfirmBody}
            </p>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}

/** Колко ще е пълен бусът след заявката — по по-пълното от тегло и обем. */
function fillOf(candidate: DispatchCandidate): { full: boolean; note: string } {
  const cap = candidate.capacity;
  const w = cap.payloadKg ? Math.round((cap.afterKg * 100) / cap.payloadKg) : null;
  const v = cap.cargoVolumeM3 ? Math.round((cap.afterM3 * 100) / cap.cargoVolumeM3) : null;
  const parts = [w !== null ? `${w} % тегло` : null, v !== null ? `${v} % обем` : null].filter(Boolean);
  return { full: (w ?? 0) >= FULL_PCT || (v ?? 0) >= FULL_PCT, note: parts.join(', ') };
}

function ModeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-1.5 text-xs transition-colors',
        active ? 'border-accent bg-accent-soft text-accent' : 'border-line-strong text-ink-dim hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-faint">
        <Icon className="size-3" />
        {label}
      </dt>
      <dd className="truncate font-mono text-xs tabular text-ink">{value}</dd>
    </div>
  );
}
